<template>
  <v-container fluid class="page--crate-battle" :class="pageClasses">
    <v-container class="page-container">
      <v-row class="pt-10">
        <!-- header -->
        <v-col cols="12" class="mb-8">
          <v-row>
            <v-col
              cols="6" order="1"
              md="3" order-md="1"
              align-self="center"
            >
              <router-link :to="{name: 'Crate Battles'}" class="link text--secondary text-uppercase text-body-2">
                <fai class="mr-2" :icon="['fad', 'long-arrow-left']" />
                <span v-t="'page.case_battles.back_to_battles'" />
              </router-link>
              <v-spacer />
            </v-col>

            <!-- case info -->
            <v-col
              cols="12" order="3"
              md="6" order-md="2"
              align-self="center"
              class="text-center"
            >
              <h4 v-if="game" class="text-h6 font-weight-bold lh-1" v-text="game.title" />
              <v-skeleton-loader v-else type="heading" class="d-flex justify-center" />

              <!-- cursed mode -->
              <v-tooltip v-if="game && game.isCursed" bottom content-class="rounded-0" color="primary500">
                <template #activator="{ on }">
                  <h5 class="primary500--text text-body-2 text-uppercase font-weight-bold mt-2 lh-1" v-on="on">
                    <fai :icon="['fad', 'skull-crossbones']" />
                    Cursed mode
                  </h5>
                </template>
                <span>Team with the least unboxed wins &ndash; loser wins!</span>
              </v-tooltip>
            </v-col>

            <!-- share -->
            <v-col
              cols="6" order="2"
              md="3" order-md="3"
              class="d-flex justify-end align-end"
            >
              <v-btn
                :ripple="false" depressed tile
                color="grey400 text--secondary px-3"
                @click="onShare"
              >
                <div class="d-flex align-center">
                  <fai fixed-width :icon="['fad', 'share-alt-square']" class="blue300--text mr-1 text-h6" />
                  <h4 class="font-weight-bold">
                    Share
                  </h4>
                </div>
              </v-btn>

              <v-tooltip top content-class="rounded-0">
                <template #activator="{ on }">
                  <v-btn
                    :ripple="false" depressed tile smalls
                    class="grey400 px-3 unwidth ml-2"
                    :class="muted ? 'primary500--text' : 'text--secondary'"
                    v-on="on"
                    @click="onToggleSounds(muted)"
                  >
                    <fai fixed-width :icon="['fas', muted ? 'volume-slash' : 'volume-up']" />
                  </v-btn>
                </template>
                <span>Sounds</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <v-alert
            v-if="game && game.private" color="blue800" dense class="mt-4 mb-n4 text-center"
            tile
          >
            <fai fixed-width :icon="['fad', 'lock']" class="blue200--text text-body-1 mr-2" />
            <span class="text-body-2">
              This game is private and hidden, share the link to invite your friends.
            </span>
          </v-alert>
        </v-col>

        <!-- spinner -->
        <v-col v-if="game" cols="12" class="battle-spinner-container pb-sm-0">
          <v-row dense class="spinner-header mb-1">
            <v-col
              cols="6" order="1"
              md="3" order-md="1"
              align-self="end"
              class="text-body-2 text--secondary text-uppercase px-4"
            >
              Total <scrap class="ml-1" :amount="game.price" />
            </v-col>

            <!-- rounds info -->
            <v-col
              cols="12" order="3"
              md="6" order-md="2"
              align-self="center"
              class="d-flex justify-center mb-n8"
            >
              <CrateBattleRounds class="flex-grow-1" :game="game" :crate-size="52" @inspect="onInspectCrate" />
            </v-col>

            <!-- share -->
            <v-col
              cols="6" order="2"
              md="3" order-md="3"
              class="text-right text-caption px-4"
              align-self="end"
            >
              <router-link :to="{name: 'fairness'}" class="link d-inline-flex align-center">
                <fai :icon="['fad', 'file-contract']" class="mr-2 text-body-1 green400--text" />
                <h4 class="text-body-2 mt-1 text--secondary">
                  Fairness
                </h4>
              </router-link>
            </v-col>
          </v-row>

          <div class="spinner-ctn grey900">
            <canvas class="confetti" />

            <CrateBattleSpinnerOverlay
              ref="spinnerOverlay"
              :active="showSpinnerOverlay"
              :game="game" :config="config"
              :muted="muted"
            />

            <CrateBattleSpinner
              ref="spinner" :game="game" :itemdb="itemdb" :config="config"
              :muted="muted"
            />
          </div>
        </v-col>

        <!-- teams -->
        <v-col v-if="game" cols="12" class="pt-sm-0">
          <v-row dense class="teams pos-relative">
            <div v-show="game.teams[0].slots > 1 && !game.isUneven && !game.isCommunism" class="team-vs bg-ui-panel--light px-2">
              <span class="text--secondary text-h6 font-weight-bold">vs</span>
            </div>

            <v-col v-for="team in game.teams" :key="team.id" :cols="12 * (team.slots / game.config.slots)">
              <div class="team-header mt-n6 mx-auto" :class="`team-id-${team.id} team-count-${team.slots}`">
                <CrateBattleSlotHeader
                  v-for="slot in team.slots" :key="slot"
                  :team-slot="slot" :team="team" :game="game" :config="config"
                  :loading="loading.bot || loading.join" :is-creator="isCreator" :can-join="canJoin"
                  :won-amount="winningTeams.includes(team.id) ? game.wonPerPlayer : null"
                  :show-winnings="winnersAnnounced"
                  @click.native="onClickTeamSlot(team.id, slot)"
                />
              </div>
            </v-col>
          </v-row>
        </v-col>

        <!-- items -->
        <v-col v-if="game" cols="12" class="px-0">
          <v-container ref="gameItemsCtn" fluid class="game-items pos-relative">
            <v-row>
              <v-col v-for="team in game.teams" :key="team.id" :cols="12 * (team.slots / game.config.slots)" class="py-0">
                <div class="team-items d-flex">
                  <div v-for="slot in team.slots" :key="slot" class="full-width player-items">
                    <CrateBattleRoundItem
                      v-for="round in game.rounds.length"
                      :key="round"
                      :round="round"
                      :image-size="72"
                      :item="teamSlotItems(team.id, slot)[round - 1]"
                    />
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </v-col>

        <v-col v-if="game" cols="12" class="text--secondary">
          <v-divider class="my-8" />
          <h4 class="text-uppercase text-body-2 mb-2 font-weight-semibold">
            Fairness
          </h4>

          <h5 class="text-caption">
            <b class="mr-5 text-overline lh-1">HASH</b> {{ game.fairness.hash }}
          </h5>
          <h5 v-if="game.fairness.secret" class="text-caption">
            <b class="mr-2 text-overline lh-1">SECRET</b> {{ game.fairness.secret }}
          </h5>
          <h5 class="text-caption">
            <b class="mr-8 text-overline lh-1">EOS</b>
            <a
              v-if="game.fairness.solvingBlockId"
              class="link lh-1 blue300--text"
              rel="noopener noreferrer"
              target="_blank"
              :href="`https://bloks.io/block/${game.fairness.solvingBlockId}`"
            >
              #{{ game.fairness.solvingBlockId }}
            </a>
            <span v-else class="text--disabled">Game not locked yet</span>
          </h5>
        </v-col>
      </v-row>
    </v-container>
  </v-container>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import OnboardingMixin from '@/mixins/onboarding'
import CaseBattlesGame from '@/store/models/games/case-battles/CaseBattlesGame'
import CrateBattleRounds from './components/CrateBattleRounds'
import CrateBattleSlotHeader from './components/CrateBattleSlotHeader'
import CrateBattleRoundItem from './components/CrateBattleRoundItem'
import CrateBattleSpinner from './components/CrateBattleSpinner'
import CrateBattleSpinnerOverlay from './components/CrateBattleSpinnerOverlay'
import { GAME_STATUS } from '../utils'

// TODO automatically close backpack if 4 player game & battle area under 1400px ?

export default {
  metaInfo() {
    return {
      title: this.game?.title ?? 'Crate Battle',
    }
  },
  components: {
    CrateBattleRounds,
    CrateBattleSlotHeader,
    CrateBattleRoundItem,
    CrateBattleSpinner,
    CrateBattleSpinnerOverlay,
  },
  mixins: [OnboardingMixin],
  async beforeRouteUpdate(to, from, next) {
    this.game?.$update({ dontDelete: false })

    await this.onEnterPage(to.params.id)
    next()
    this.init()
  },
  data() {
    return {
      loading: {
        bot: false,
        join: false,
      },
      rollFinished: false,
      winnersAnnounced: false,
    }
  },
  computed: {
    ...mapGetters({
      conga: 'conga/conga',
      user: 'user/session',
      itemdb: 'items/items',
      settings: 'settings/settings',
    }),
    gameId() {
      return this.$route.params?.id ?? null
    },
    game() {
      return CaseBattlesGame.find(this.gameId)
    },
    config() {
      return this.conga.games.caseBattles
    },
    GAME_STATUS() {
      return GAME_STATUS
    },
    isCreator() {
      if (!this.user) return false

      return this.user.steamid === this.game?.creatorId
    },
    pageClasses() {
      if (!this.game) return {}

      return {
        'is-3-player': this.game.config.slots === 3,
        'is-4-player': this.game.config.slots === 4,
        'is-communism': this.game.isCommunism,
      }
    },
    canBanditsJoin() {
      return false
    },
    crateWeights() {
      return Object.values(this.game.config.cases).reduce((acc, crate) => {
        acc[crate.id] = crate.items.reduce((bcc, v) => bcc + v.weight, 0)
        return acc
      }, {})
    },
    playerItems() {
      const {
        round, config, items, status,
      } = this.game
      const { itemdb, rollFinished, crateWeights } = this

      if (!items.length) return {}

      return items.reduce((acc, item) => {
        if (!acc[item.playerId]) {
          acc[item.playerId] = []
        }

        if (status === GAME_STATUS.rolling) {
          if (item.round >= round && !rollFinished) return acc
        }

        const crate = config.cases[config.rounds[item.round - 1]]
        const crateItem = crate.items.find(i => i.id === item.id)

        acc[item.playerId].push({
          ...itemdb[item.id],
          ...crateItem,
          ...item,
          odds: crateItem.weight / crateWeights[crate.id],
        })

        return acc
      }, {})
    },
    showSpinnerOverlay() {
      const { status } = this.game
      return status !== GAME_STATUS.rolling
    },
    winningTeams() {
      return this.game.data.winningTeams ?? []
    },
    canJoin() {
      return !this.game.players[this.user?.steamid]
    },
    //
    muted() {
      return !this.settings.games.caseBattles.sounds
    },
  },
  watch: {
    'game.round': function onGameRound(round) {
      if (this.game.status !== GAME_STATUS.rolling) return
      this.onGameRound(round)
    },
    'game.status': async function onStatusChange(status, oldStatus) {
      if (oldStatus === GAME_STATUS.rolling && status === GAME_STATUS.complete) {
        this.onTallyWinners()
      }
    },
  },
  beforeDestroy() {
    this.game?.$update({ dontDelete: false })
  },
  async created() {
    await this.onEnterPage(this.gameId)
    this.init()
  },
  methods: {
    ...mapActions({
      getGameById: 'games/case-battles/getById',
      requestBot: 'games/case-battles/requestBot',
      join: 'games/case-battles/join',
    }),
    ...mapMutations({
      setSettings: 'settings/setSettings',
    }),
    init() {
      if (this.game?.canShowWinners) {
        this.rollFinished = true
      }

      if (this.game.status === GAME_STATUS.rolling) {
        this.onGameRound(this.game.round)
      }
      if (this.game.status === GAME_STATUS.complete) {
        this.onTallyWinners()
      }
    },
    async onEnterPage(gameId) {
      try {
        const manuallyFetch = !this.game

        if (manuallyFetch) {
          await this.getGameById(gameId)
        }

        if (!this.game) throw new Error('GameNotFound')

        // make sure it doesnt get garbage collected
        this.game.$update({ dontDelete: true })
      } catch (err) {
        this.$router.push({ name: '404', params: { 0: this.$route.path } })
        this.$toast.error(err.message)
      }
    },
    onClickTeamSlot(teamId, slot) {
      if (this.game.playersByTeam(teamId)?.[slot - 1]) return

      const team = this.game.teams.find(i => i.id === teamId)
      const teamPlayers = this.game.playersByTeam(teamId)

      if (teamPlayers.length >= team.slots) return

      if (this.isCreator) {
        if (this.game.banditsAvailable(this.config.bot)) {
          this.onCallBandit(teamId)
        }
        return
      }

      if (!this.canJoin) return

      this.onJoin(teamId)
    },
    async onCallBandit(teamId) {
      if (this.loading.bot) return

      try {
        this.loading.bot = true
        await this.requestBot({ id: this.game.id, teamId })
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.bot = false
      }
    },
    async onJoin(teamId) {
      if (!this.user) {
        this.$modal.open('auth', { maxWidth: 320, persistent: true })
        return
      }

      const hasWarning = this.game.isUneven || this.game.isCursed
      if (hasWarning && !this.onboardingHasSeen('battle-join-warning')) {
        this.$modal.open('battle-join', { maxWidth: 350 }, { game: this.game, teamId })
        return
      }

      if (this.loading.join) return

      try {
        this.loading.join = true
        await this.join({ id: this.game.id, teamId })
      } catch (error) {
        this.$toast.error(error.message)
      } finally {
        this.loading.join = false
      }
    },
    async onGameRound(round) {
      const { itemdb } = this

      const roundItems = this.game.items.filter(i => i.round === round).reduce((acc, item) => {
        acc[item.playerId] = { ...itemdb[item.id], ...item }
        return acc
      }, {})


      const timeSinceRoll = Date.now() - this.game.lastRoundAt
      this.rollFinished = timeSinceRoll > this.config.time.roll + 600

      // wait for props to update on spinner
      await this.$nextTick()
      this.$refs.spinner.spin(roundItems, timeSinceRoll)

      // scroll down game items
      const $gameItemsCtn = this.$refs.gameItemsCtn
      if ($gameItemsCtn) {
        const $roundItem = $gameItemsCtn.querySelector(`.round-item.round-${round}`)
        if ($roundItem) {
          $gameItemsCtn.scrollTo({
            top: $roundItem.offsetTop,
            behavior: 'smooth',
          })
        }
      }

      if (!this.rollFinished) {
        setTimeout(() => {
          this.rollFinished = true
        }, this.config.time.roll - timeSinceRoll + 600)
      }
    },
    async onTallyWinners() {
      const timeSinceTally = Date.now() - this.game.finishedAt
      this.winnersAnnounced = timeSinceTally > this.config.time.tally

      // wait for props to update on spinner
      await this.$nextTick()
      const anim = this.$refs.spinnerOverlay.tallyWinners(timeSinceTally)

      if (!this.winnersAnnounced) {
        anim.finished.then(() => {
          this.winnersAnnounced = true
        })

        setTimeout(() => {
          this.winnersAnnounced = true
        }, this.config.time.tally - timeSinceTally)
      }
    },
    onInspectCrate(crate) {
      this.$modal.open('battle-inspect-crate', { maxWidth: 650 }, { crate })
    },
    //
    teamSlotItems(teamId, slot) {
      return this.playerItems[this.game.playersByTeam(teamId)?.[slot - 1]?.steamid] ?? []
    },
    onToggleSounds(val) {
      this.settings.games.caseBattles.sounds = val
      this.setSettings(this.settings)
    },
    async onShare() {
      if (!navigator.share) {
        if (!navigator.clipboard) {
          this.$toast.error('Your browser doesn\'t support this feature.')
          return
        }

        try {
          await navigator.clipboard.writeText(document.location.href)
          this.$toast.info('Link copied to clipboard')
        } catch (err) {
          console.error(err)
          this.$toast.error('Copy failed, sorry.')
        }
      } else {
        await navigator.share({
          title: `${this.game.title} on bandit.camp`,
          text: `${this.game.title} on bandit.camp`,
          url: document.location.href,
        })
      }
    },
  },
}

</script>

<style lang="scss" scoped src="./battle.scss"></style>
